import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  CreditCard,
  QrCode,
  Clock,
  Calendar,
  X,
  Camera,
  FileText,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { QrCodeScanner } from '../../components/banking/QrCodeScanner';

type PaymentTab = 'boleto' | 'qrcode' | 'scheduled' | 'history';

export function CustomerPayments() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<PaymentTab>('qrcode');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Boleto form state
  const [barcode, setBarcode] = useState('');
  const [boletoData, setBoletoData] = useState<any>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  
  // QR Code form state
  const [qrCodeValue, setQrCodeValue] = useState('');
  const [qrCodeData, setQrCodeData] = useState<any>(null);
  const [showScanner, setShowScanner] = useState(false);

  // Fetch scheduled payments
  const { data: scheduledPayments, isLoading: scheduledLoading } = useQuery({
    queryKey: ['payments', 'scheduled'],
    queryFn: async () => {
      const response = await api.get('/banking/payments/scheduled');
      return response.data;
    },
    enabled: activeTab === 'scheduled',
  });

  // Fetch payment history
  const { data: paymentHistory, isLoading: historyLoading } = useQuery({
    queryKey: ['payments', 'history'],
    queryFn: async () => {
      const response = await api.get('/banking/payments/history');
      return response.data;
    },
    enabled: activeTab === 'history',
  });

  // Validate barcode mutation
  const validateBarcodeMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/banking/payments/validate-barcode', { barcode: code });
      return response.data;
    },
    onSuccess: (data) => {
      setBoletoData(data);
    },
  });

  // Validate QR Code mutation
  const validateQrMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await api.post('/banking/payments/validate-qr', { qrCode: code });
      return response.data;
    },
    onSuccess: (data) => {
      setQrCodeData(data);
    },
  });

  // Pay boleto mutation
  const payBoletoMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/payments/boleto', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['payments', 'history'] });
      setShowConfirmModal(false);
      resetForm();
    },
  });

  // Pay QR Code mutation
  const payQrMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/payments/qr', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banking', 'balance'] });
      queryClient.invalidateQueries({ queryKey: ['payments', 'history'] });
      setShowConfirmModal(false);
      resetForm();
    },
  });

  // Schedule payment mutation
  const schedulePaymentMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/banking/payments/schedule', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'scheduled'] });
      resetForm();
    },
  });

  // Cancel scheduled payment mutation
  const cancelScheduledMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/banking/payments/scheduled/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments', 'scheduled'] });
    },
  });

  const resetForm = () => {
    setBarcode('');
    setBoletoData(null);
    setQrCodeValue('');
    setQrCodeData(null);
    setScheduleDate('');
  };

  const handleQrScanned = (decodedText: string) => {
    setQrCodeValue(decodedText);
    setShowScanner(false);
    if (decodedText.length > 10) {
      validateQrMutation.mutate(decodedText);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(date));
  };

  const handleBarcodeChange = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    setBarcode(cleaned);
    if (cleaned.length >= 44) {
      validateBarcodeMutation.mutate(cleaned);
    } else {
      setBoletoData(null);
    }
  };

  const handlePayBoleto = () => {
    if (scheduleDate) {
      schedulePaymentMutation.mutate({
        type: 'BOLETO',
        barcode,
        scheduledDate: scheduleDate,
      });
    } else {
      payBoletoMutation.mutate({ barcode });
    }
  };

  const handlePayQr = () => {
    payQrMutation.mutate({ qrCode: qrCodeValue });
  };

  const tabs = [
    { id: 'qrcode' as PaymentTab, label: 'QR Code', icon: <QrCode size={18} /> },
    { id: 'history' as PaymentTab, label: 'Histórico', icon: <CreditCard size={18} /> },
  ];


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Pagamentos</h1>
        <p className="text-surface-400">Pague boletos e QR Codes</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap
              transition-colors
              ${activeTab === tab.id
                ? 'bg-primary-500 text-white'
                : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
              }
            `}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Boleto Tab */}
      {activeTab === 'boleto' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Pagar Boleto</h2>

            <Input
              label="Código de barras"
              placeholder="Digite ou cole o código de barras"
              value={barcode}
              onChange={(e) => handleBarcodeChange(e.target.value)}
              leftIcon={<FileText size={18} className="text-surface-400" />}
            />

            {validateBarcodeMutation.isPending && (
              <div className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                <div className="h-3 w-48 bg-surface-700 rounded" />
              </div>
            )}

            {boletoData && (
              <div className="p-4 rounded-lg bg-surface-800 space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Beneficiário</span>
                  <span className="text-white">{boletoData.beneficiary}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor</span>
                  <span className="text-white font-semibold">
                    {formatCurrency(boletoData.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Vencimento</span>
                  <span className={`
                    ${new Date(boletoData.dueDate) < new Date() ? 'text-red-400' : 'text-white'}
                  `}>
                    {formatDate(boletoData.dueDate)}
                  </span>
                </div>
                {boletoData.discount && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Desconto</span>
                    <span className="text-green-400">
                      -{formatCurrency(boletoData.discount)}
                    </span>
                  </div>
                )}
                {boletoData.fine && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Multa/Juros</span>
                    <span className="text-red-400">
                      +{formatCurrency(boletoData.fine)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {boletoData && (
              <>
                <Input
                  label="Agendar para (opcional)"
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  leftIcon={<Calendar size={18} className="text-surface-400" />}
                />

                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={payBoletoMutation.isPending}
                >
                  {scheduleDate ? 'Agendar Pagamento' : 'Pagar Agora'}
                </Button>
              </>
            )}
          </div>
        </Card>
      )}

      {/* QR Code Tab */}
      {activeTab === 'qrcode' && (
        <Card>
          <div className="p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white">Pagar com QR Code</h2>

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={() => setShowScanner(true)}
                className="w-full p-8 rounded-lg border-2 border-dashed border-surface-700 hover:border-primary-500 transition-colors cursor-pointer"
              >
                <Camera size={48} className="mx-auto text-surface-500 mb-3" />
                <p className="text-surface-400">Clique para escanear QR Code</p>
                <p className="text-xs text-surface-500 mt-1">ou cole o código abaixo</p>
              </button>

              <div className="w-full">
                <Input
                  label="Código Pix Copia e Cola"
                  placeholder="Cole o código Pix aqui"
                  value={qrCodeValue}
                  onChange={(e) => {
                    setQrCodeValue(e.target.value);
                    if (e.target.value.length > 10) {
                      validateQrMutation.mutate(e.target.value);
                    }
                  }}
                />
              </div>
            </div>

            {validateQrMutation.isPending && (
              <div className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                <div className="h-3 w-48 bg-surface-700 rounded" />
              </div>
            )}

            {qrCodeData && (
              <div className="p-4 rounded-lg bg-surface-800 space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Recebedor</span>
                  <span className="text-white">{qrCodeData.recipientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor</span>
                  <span className="text-white font-semibold">
                    {formatCurrency(qrCodeData.amount)}
                  </span>
                </div>
                {qrCodeData.description && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Descrição</span>
                    <span className="text-white">{qrCodeData.description}</span>
                  </div>
                )}
              </div>
            )}

            {qrCodeData && (
              <Button
                variant="primary"
                className="w-full"
                onClick={handlePayQr}
                disabled={payQrMutation.isPending}
              >
                Pagar
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Scheduled Tab */}
      {activeTab === 'scheduled' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Pagamentos Agendados</h2>

            {scheduledLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                    <div className="h-3 w-48 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : scheduledPayments?.items?.length > 0 ? (
              <div className="space-y-3">
                {scheduledPayments.items.map((payment: any) => (
                  <div
                    key={payment.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-surface-800/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <Clock size={20} className="text-primary-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {payment.beneficiary || 'Pagamento'}
                        </p>
                        <p className="text-xs text-surface-400">
                          Agendado para {formatDate(payment.scheduledDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-semibold text-white">
                        {formatCurrency(payment.amount)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelScheduledMutation.mutate(payment.id)}
                        disabled={cancelScheduledMutation.isPending}
                      >
                        <X size={16} className="text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum pagamento agendado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Histórico de Pagamentos</h2>

            {historyLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-surface-700" />
                    <div className="flex-1">
                      <div className="h-4 w-32 bg-surface-700 rounded mb-2" />
                      <div className="h-3 w-24 bg-surface-700 rounded" />
                    </div>
                    <div className="h-5 w-20 bg-surface-700 rounded" />
                  </div>
                ))}
              </div>
            ) : paymentHistory?.items?.length > 0 ? (
              <div className="space-y-2">
                {paymentHistory.items.map((payment: any) => (
                  <div
                    key={payment.id}
                    className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-800 transition-colors cursor-pointer"
                  >
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      ${payment.status === 'COMPLETED'
                        ? 'bg-green-500/20 text-green-400'
                        : payment.status === 'FAILED'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                      }
                    `}>
                      {payment.status === 'COMPLETED'
                        ? <Check size={20} />
                        : payment.status === 'FAILED'
                        ? <AlertTriangle size={20} />
                        : <Clock size={20} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {payment.beneficiary || payment.type}
                      </p>
                      <p className="text-xs text-surface-400">
                        {formatDate(payment.createdAt)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {formatCurrency(payment.amount)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCard size={48} className="mx-auto text-surface-600 mb-3" />
                <p className="text-surface-400">Nenhum pagamento realizado</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Confirm Payment Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirmar Pagamento"
      >

      {/* QR Code Scanner */}
      <QrCodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleQrScanned}
      />
        <div className="space-y-4">
          {boletoData && (
            <div className="p-4 rounded-lg bg-surface-800">
              <div className="flex justify-between mb-2">
                <span className="text-surface-400">Beneficiário</span>
                <span className="text-white">{boletoData.beneficiary}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-surface-400">Valor</span>
                <span className="text-white font-semibold">
                  {formatCurrency(boletoData.amount)}
                </span>
              </div>
              {scheduleDate && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Agendado para</span>
                  <span className="text-white">{formatDate(scheduleDate)}</span>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowConfirmModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handlePayBoleto}
              disabled={payBoletoMutation.isPending || schedulePaymentMutation.isPending}
            >
              {payBoletoMutation.isPending || schedulePaymentMutation.isPending
                ? 'Processando...'
                : 'Confirmar'
              }
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CustomerPayments;
